import { FORMAT_LABELS, FormatSchema, type Deck, type Format } from '@ugin/shared';
import { Loader2 } from 'lucide-react';
import { useState, type SyntheticEvent } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useCreateDeck } from '@/hooks/useDecks';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported?: (deck: Deck) => void;
}

export function ImportDeckDialog({ open, onOpenChange, onImported }: Props) {
  const [name, setName] = useState('');
  const [format, setFormat] = useState<Format>('standard');
  const [paste, setPaste] = useState('');
  const create = useCreateDeck();

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    try {
      const res = await create.mutateAsync({
        name: name.trim() || undefined,
        format,
        arenaPaste: paste,
        ownerId: 'rowan',
      });
      onImported?.(res.deck);
      setPaste('');
      setName('');
    } catch (err) {
      // Toast handled by the parent or fallback below.
      console.error(err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Import deck</DialogTitle>
          <DialogDescription>
            Paste an Arena export. Ugin will resolve every card against Scryfall.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_180px]">
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="text-ugin-text-muted">Name (optional)</span>
              <Input
                value={name}
                onChange={(e) => { setName(e.target.value); }}
                placeholder="Mono-Red Aggro"
              />
            </label>
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="text-ugin-text-muted">Format</span>
              <Select value={format} onValueChange={(v) => { setFormat(v as Format); }}>
                <SelectTrigger aria-label="Format">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FormatSchema.options.map((f) => (
                    <SelectItem key={f} value={f}>
                      {FORMAT_LABELS[f]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          </div>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-ugin-text-muted">Arena export</span>
            <Textarea
              value={paste}
              onChange={(e) => { setPaste(e.target.value); }}
              placeholder={'4 Lightning Bolt (M21) 162\n4 Monastery Swiftspear (M21) 162\n20 Mountain\n\nSideboard\n3 Pyroblast'}
              className="min-h-[220px]"
              required
              aria-label="Paste Arena export"
            />
          </label>

          {create.error ? (
            <p className="text-sm text-ugin-danger" role="alert">
              {create.error instanceof Error ? create.error.message : 'Import failed.'}
            </p>
          ) : null}

          <DialogFooter>
            <Button
              variant="ghost"
              type="button"
              onClick={() => { onOpenChange(false); }}
              disabled={create.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending || !paste.trim()}>
              {create.isPending ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Importing…
                </>
              ) : (
                'Import'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
